/**
 * Workability Resolver (Spoor 2, Verlof MVP — AD-5)
 *
 * THE single, shared resolver for `datum -> (weekType A/B, werkbaarheid per
 * dagdeel)`. Backend `leaveRequestService`, `calendarService` (Story 4.1),
 * and the frontend aanvraagformulier (Story 1.3, live preview) all import
 * these exact functions — nobody re-implements A/B or dagdeel-werkbaarheid
 * logic elsewhere. Deliberately NOT the frontend-only, ISO-week-based
 * `features/werkrooster/utils/dateUtils.ts:getWeekType` (see AD-5: that
 * function uses ISO-week-number parity, which breaks across a 53-ISO-week
 * year boundary -- this resolver uses anchor-date parity instead).
 */
import { SCHEDULE_ANCHOR_DATE } from '../config/scheduleAnchor.js';
import { beslistAlleenHr, soortTeltMeeInMaandruimte, VERLOF_KOPPEL_REDENEN, VERLOF_KOPPEL_REDENEN_OPNIEUW_TE_PROBEREN, } from '../types/leave.js';
/**
 * Hours represented by a single dagdeel (half of a standard 8-hour
 * workday). Deliberately a fixed constant, not derived from
 * `work_schedule_entries.startTime/endTime` -- per-employee contract-hour
 * variation is out of scope for this MVP resolver (documented
 * simplification; a future story can parameterize this per employee if
 * variable contract hours turn out to matter for uren-voorrekening).
 */
const HOURS_PER_DAGDEEL = 4;
function toUtcDate(value) {
    if (value instanceof Date) {
        return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
    }
    const parts = value.split('-').map(Number);
    const year = parts[0] ?? 1970;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return new Date(Date.UTC(year, month - 1, day));
}
function isoDateString(d) {
    return d.toISOString().slice(0, 10);
}
function mondayOfWeek(d) {
    const day = d.getUTCDay(); // 0=zondag
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() + diff);
    return monday;
}
/**
 * Resolve the A/B week type for a date via anchor-date parity: the number
 * of whole weeks between the anchor's Monday and the date's Monday, modulo
 * 2. NOT ISO-week-number parity (AD-5).
 */
export function resolveWeekType(date, anchorDate = SCHEDULE_ANCHOR_DATE) {
    const targetMonday = mondayOfWeek(toUtcDate(date));
    const anchorMonday = mondayOfWeek(toUtcDate(anchorDate));
    const diffMs = targetMonday.getTime() - anchorMonday.getTime();
    const wholeWeeks = Math.round(diffMs / (7 * 86_400_000));
    const parity = ((wholeWeeks % 2) + 2) % 2; // safe modulo for negative offsets (dates before the anchor)
    return parity === 0 ? 'A' : 'B';
}
/**
 * Resolve per-dagdeel workability for a single date: holiday overrides
 * rooster (feestdag overschrijft rooster); otherwise driven by the
 * matching `work_schedule_entries` row for that week-type/day-of-week.
 */
export function resolveWorkability(date, scheduleEntries, holidays = [], anchorDate = SCHEDULE_ANCHOR_DATE) {
    const d = toUtcDate(date);
    const dateStr = isoDateString(d);
    if (holidays.some((h) => h.date === dateStr)) {
        return { morning: false, afternoon: false };
    }
    const weekType = resolveWeekType(d, anchorDate);
    const isoDayOfWeek = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    const entry = scheduleEntries.find((e) => e.weekType === weekType && e.dayOfWeek === isoDayOfWeek);
    if (!entry || !entry.isWorkday) {
        return { morning: false, afternoon: false };
    }
    if (entry.isHalfDay) {
        return {
            morning: entry.halfDayPeriod === 'ochtend',
            afternoon: entry.halfDayPeriod === 'middag',
        };
    }
    return { morning: true, afternoon: true };
}
/**
 * Finds the single `work_schedule_entries` row that governs a given date
 * (Story 1.3): the exact same week-type/day-of-week resolution
 * `resolveWorkability` uses internally, exposed separately because this
 * caller needs the row itself (its `startTime`/`endTime`, for the van-tot
 * roosterspan and the AC-9 reference line) rather than a workable/
 * not-workable boolean. Returns `undefined` when there is no matching row --
 * either no schedule data at all for the employee (AC-11) or none for this
 * specific week-type/day-of-week combination.
 */
export function findScheduleEntryForDate(date, scheduleEntries, anchorDate = SCHEDULE_ANCHOR_DATE) {
    const d = toUtcDate(date);
    const weekType = resolveWeekType(d, anchorDate);
    const isoDayOfWeek = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    return scheduleEntries.find((e) => e.weekType === weekType && e.dayOfWeek === isoDayOfWeek);
}
/**
 * Calculate the total leave hours for a date range with dagdeel precision.
 * Edge days use their own `startPart`/`endPart`; days strictly between the
 * start and end date are treated as `hele_dag` requests. The hours for
 * each day are the intersection of the requested dagdeel(en) and the
 * workable dagdeel(en) for that day (AD-5 ijkgevallen: a half-day rooster
 * entry never contributes more than 1 dagdeel; a dagdeel that isn't
 * workable contributes 0, even if requested).
 */
export function calculateLeaveHours(startDate, endDate, startPart, endPart, scheduleEntries, holidays = [], anchorDate = SCHEDULE_ANCHOR_DATE) {
    const start = toUtcDate(startDate);
    const end = toUtcDate(endDate);
    const startStr = isoDateString(start);
    const endStr = isoDateString(end);
    let totalHours = 0;
    for (let cursor = new Date(start); cursor.getTime() <= end.getTime(); cursor.setUTCDate(cursor.getUTCDate() + 1)) {
        const dateStr = isoDateString(cursor);
        const isStartDay = dateStr === startStr;
        const isEndDay = dateStr === endStr;
        const requestedPart = isStartDay ? startPart : isEndDay ? endPart : 'hele_dag';
        const workability = resolveWorkability(cursor, scheduleEntries, holidays, anchorDate);
        let dayHours = 0;
        if (requestedPart === 'hele_dag') {
            if (workability.morning)
                dayHours += HOURS_PER_DAGDEEL;
            if (workability.afternoon)
                dayHours += HOURS_PER_DAGDEEL;
        }
        else if (requestedPart === 'ochtend' && workability.morning) {
            dayHours += HOURS_PER_DAGDEEL;
        }
        else if (requestedPart === 'middag' && workability.afternoon) {
            dayHours += HOURS_PER_DAGDEEL;
        }
        totalHours += dayHours;
    }
    return totalHours;
}
const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** Parses `"HH:MM"` to minutes since midnight, or `null` for anything else. */
function timeToMinutesOfDay(value) {
    const match = TIME_OF_DAY_PATTERN.exec(value);
    if (!match)
        return null;
    return Number(match[1]) * 60 + Number(match[2]);
}
/**
 * Story 10.2, AC-5 — HET aantal WERKDAGEN in een periode.
 *
 * ÉÉN AFLEIDING, TWEE LEZERS (KE-9). Het aanvraagformulier van de medewerker
 * en de goedkeuringslijst van HR stellen dezelfde vraag; twee tellingen die
 * hetzelfde heten en anders uitkomen is het ergste wat hier kan gebeuren, want
 * dan ziet HR een afwijking die de medewerker niet zag (story 10.3, AC-2).
 *
 * DE WERKBARE DAGEN WORDEN GETELD, DE UREN WORDEN NIET GEDEELD. Uren delen
 * door acht breekt zodra er een halve werkdag in de periode valt: 36 uur over
 * vijf werkbare dagen levert dan 4,5 op, en *"4,5 werkdagen"* is geen getal
 * dat de vastgestelde zin uit §10.3 kan dragen. Een dag telt mee zodra er íets
 * werkbaars op staat — een halve dag is een werkdag.
 *
 * Weekenden, feestdagen en roostervrije dagen leveren niets op en schuiven
 * niets door (FR-13); dat volgt uit `resolveWorkability` en niet uit een eigen
 * kalenderregel hier.
 */
export function werkbareDagenInPeriode(startDate, endDate, scheduleEntries, holidays = [], anchorDate = SCHEDULE_ANCHOR_DATE) {
    const start = toUtcDate(startDate);
    const end = toUtcDate(endDate);
    let aantal = 0;
    for (let cursor = new Date(start); cursor.getTime() <= end.getTime(); cursor.setUTCDate(cursor.getUTCDate() + 1)) {
        const dag = resolveWorkability(cursor, scheduleEntries, holidays, anchorDate);
        if (dag.morning || dag.afternoon)
            aantal += 1;
    }
    return aantal;
}
/**
 * Story 10.4, AC-2 — de einddatum die vanaf `startDate` precies
 * `aantalWerkdagen` WERKDAGEN oplevert, de begindag meegeteld.
 *
 * HET HANDBOEK KENT WERKDAGEN, HET FORMULIER VULT EEN EINDDATUM. Vijf
 * werkdagen vanaf de dag van overlijden is zelden vijf kalenderdagen: er zitten
 * weekenden, feestdagen en roostervrije dagen tussen. Vandaar deze afleiding,
 * naast {@link werkbareDagenInPeriode} — de omgekeerde vraag, met dezelfde
 * werkbaarheidsregel.
 *
 * Levert de begindatum zelf niets werkbaars op, dan schuift de periode door
 * naar de eerstvolgende dag die dat wél doet. Wordt er binnen een jaar geen
 * enkele werkbare dag gevonden (een medewerker zonder rooster), dan komt de
 * begindatum terug: liever een periode van één dag dan een datum die nergens
 * op slaat.
 */
export function einddatumNaWerkdagen(startDate, aantalWerkdagen, scheduleEntries, holidays = [], anchorDate = SCHEDULE_ANCHOR_DATE) {
    if (aantalWerkdagen <= 0)
        return isoDateString(toUtcDate(startDate));
    const cursor = toUtcDate(startDate);
    let gevonden = 0;
    // Eén jaar is ruim: het grootste recht uit het handboek is vijf werkdagen.
    for (let stap = 0; stap < 366; stap += 1) {
        const dag = resolveWorkability(cursor, scheduleEntries, holidays, anchorDate);
        if (dag.morning || dag.afternoon) {
            gevonden += 1;
            if (gevonden === aantalWerkdagen)
                return isoDateString(cursor);
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return isoDateString(toUtcDate(startDate));
}
const ONGELDIG_RESULTAAT = {
    hours: 0,
    afgerond: false,
    volledigInPauze: false,
    buitenWerkdag: false,
    ongeldig: true,
};
/**
 * Calculates leave hours from an exact `van`-`tot` time range, minus the
 * overlapping part of the company-wide pause window, rounded to the
 * nearest quarter hour.
 *
 * AD-11 (hard): this is a SECOND, DELIBERATELY DIFFERENT hour concept from
 * `calculateLeaveHours` above, which always uses the fixed
 * `HOURS_PER_DAGDEEL` constant regardless of someone's actual rooster span.
 * The two never produce the same number for the same day by construction
 * (a half-day rooster of 08:30-12:30 yields 8 hours via `calculateLeaveHours`
 * but 4 hours via this function for the identical span) and they must NEVER
 * be mixed within one request: leave WITH times always uses this function,
 * leave WITHOUT times always uses `calculateLeaveHours`. A correction keeps
 * the concept of the original request.
 *
 * AD-15 (hard): this function is pure. `pauze` and `roosterspan` come in
 * only as arguments -- it never reads a setting, never touches the
 * database, never calls `fetch`, and never calls `new Date()` without a
 * supplied reference. That is what makes it usable identically from the
 * frontend (live preview) and the backend (server-side validation).
 *
 * Order of operations is fixed and matters (B17/B18): clamp on the rooster
 * span first, THEN subtract the overlapping pause, THEN round to the
 * nearest quarter hour. Rounding before subtracting would give a different
 * number for 11:30-13:30 with pause 12:30-13:00 than the 1.5 hours FR-2
 * uses as its reference case.
 */
export function urenUitTijdvak(van, tot, roosterspan, pauze) {
    const vanMinuten = timeToMinutesOfDay(van);
    const totMinuten = timeToMinutesOfDay(tot);
    if (vanMinuten === null || totMinuten === null || totMinuten <= vanMinuten) {
        return { ...ONGELDIG_RESULTAAT };
    }
    // 1. Clamp op de roosterspan (B18) -- indien aanwezig en geldig.
    let effectiefStart = vanMinuten;
    let effectiefEind = totMinuten;
    let buitenWerkdag = false;
    if (roosterspan) {
        const roosterStart = timeToMinutesOfDay(roosterspan.start);
        const roosterEind = timeToMinutesOfDay(roosterspan.end);
        if (roosterStart !== null && roosterEind !== null) {
            const geclampteStart = Math.max(effectiefStart, roosterStart);
            const geclampteEind = Math.min(effectiefEind, roosterEind);
            if (geclampteStart !== effectiefStart || geclampteEind !== effectiefEind) {
                buitenWerkdag = true;
            }
            effectiefStart = geclampteStart;
            effectiefEind = geclampteEind;
        }
    }
    if (effectiefEind <= effectiefStart) {
        // Het hele tijdvak viel buiten de roosterspan: niets bruikbaars over.
        return {
            hours: 0,
            afgerond: false,
            volledigInPauze: false,
            buitenWerkdag: true,
            ongeldig: false,
        };
    }
    // 2. Pauze-overlap aftrekken -- alleen het deel van de pauze dat binnen
    // het (geclampte) tijdvak valt (AC-2).
    const pauzeStart = timeToMinutesOfDay(pauze.start);
    const pauzeEind = timeToMinutesOfDay(pauze.end);
    let pauzeOverlapMinuten = 0;
    if (pauzeStart !== null && pauzeEind !== null) {
        const overlapStart = Math.max(effectiefStart, pauzeStart);
        const overlapEind = Math.min(effectiefEind, pauzeEind);
        pauzeOverlapMinuten = Math.max(0, overlapEind - overlapStart);
    }
    const tijdvakMinuten = effectiefEind - effectiefStart;
    const volledigInPauze = pauzeOverlapMinuten >= tijdvakMinuten;
    // 3. Afronden op het dichtstbijzijnde kwartier (B17).
    const ruweUren = Math.max(0, tijdvakMinuten - pauzeOverlapMinuten) / 60;
    const afgerondeUren = Math.round(ruweUren * 4) / 4;
    return {
        hours: afgerondeUren,
        afgerond: afgerondeUren !== ruweUren,
        volledigInPauze,
        buitenWerkdag,
        ongeldig: false,
    };
}
/**
 * Bovengrens voor een urencorrectie (Story 2.4, B12/AC-4/AC-5/AC-7/AC-8/AC-11).
 * Vervangt een tweede handtekening bij een ophoging: een correctie mag nooit
 * meer uren opleveren dan er die dag/periode volgens rooster NETTO wordt
 * gewerkt.
 *
 * AD-11 (hard): gebruikt altijd hetzelfde urenbegrip als de oorspronkelijke
 * aanvraag — nooit gemengd. Verlof met tijden (`aanvraag.startTime`/`endTime`
 * beide gevuld) rekent via `urenUitTijdvak` over de VOLLEDIGE roosterspan van
 * die dag (AC-5: netto, dus mét dezelfde pauze-aftrek als FR-2). Verlof
 * zonder tijden rekent via `calculateLeaveHours` — dezelfde functie die de
 * oorspronkelijke aanvraag berekende — over de hele periode (AC-7: som van
 * alle werkbare dagen, feestdagen en niet-werkbare dagen tellen niet mee) of
 * over één dagdeel (AC-8).
 *
 * AC-11: zonder (bruikbare) roosterdata voor de betrokken dag/periode geldt
 * de bestaande goedgekeurde waarde (`huidigeWaarde`) als plafond — omlaag mag
 * dan nog steeds, omhoog niet.
 *
 * AD-15 (hard): puur. Geen database, geen `new Date()` — elke datum komt
 * binnen als argument. Backend en frontend importeren dezelfde functie.
 */
export function maxCorrigeerbareUren(aanvraag, roosterregels, feestdagen, pauzeVenster, huidigeWaarde) {
    const usesTijden = aanvraag.startTime !== null && aanvraag.endTime !== null;
    if (usesTijden) {
        // AD-11: tijden zijn per constructie altijd single-day (createLeaveRequestSchema
        // staat dit alleen toe bij startDate === endDate), dus één dag is genoeg.
        const dayEntry = findScheduleEntryForDate(aanvraag.startDate, roosterregels);
        const roosterspan = dayEntry && dayEntry.startTime && dayEntry.endTime
            ? { start: dayEntry.startTime, end: dayEntry.endTime }
            : null;
        // AC-11: geen (bruikbare) roosterregel voor deze dag -- de bestaande
        // waarde blijft het plafond.
        if (!roosterspan)
            return huidigeWaarde;
        // AC-5: de volledige roosterspan zelf als tijdvak, netto (pauze-aftrek
        // via dezelfde functie als de live-voorrekening in het scherm).
        const volledigeDag = urenUitTijdvak(roosterspan.start, roosterspan.end, roosterspan, pauzeVenster);
        if (volledigeDag.ongeldig || volledigeDag.volledigInPauze)
            return huidigeWaarde;
        return volledigeDag.hours;
    }
    // AC-11: helemaal geen roosterdata bekend -- niets om de grens op te
    // baseren, dus de bestaande waarde blijft het plafond.
    if (roosterregels.length === 0)
        return huidigeWaarde;
    // AC-7/AC-8: dezelfde functie als de oorspronkelijke aanvraag, over
    // dezelfde periode/dagdelen -- meerdaags sommeert vanzelf alleen de
    // werkbare dagen, een dagdeel-aanvraag levert vanzelf maar één dagdeel op.
    return calculateLeaveHours(aanvraag.startDate, aanvraag.endDate, aanvraag.startPart, aanvraag.endPart, roosterregels, feestdagen);
}
// ── Story 2.5: Correctietermijn bewaken (AD-14, AD-25) ──
/**
 * "Vandaag" in Europe/Amsterdam, als ISO-datumstring (`YYYY-MM-DD`).
 *
 * Zelfde vorm als de bestaande `getTodayIso`-kopieën in
 * `planningService.ts`/`planningTaskService.ts`/`qualificationService.ts` —
 * de tijdzone Europe/Amsterdam, niet een vaste uuroffset, want die verschilt
 * tussen zomer- en wintertijd — maar nu als ÉÉN gedeelde functie voor de
 * hele verlofmodule (AD-25): de
 * termijn, de herinnering (epic 3), definitief-worden (epic 3) en het
 * maandoverzicht (epic 4) mogen nooit ieder een eigen "vandaag" berekenen.
 * Dit is de enige plek in deze module die de systeemklok raadpleegt — elke
 * andere functie hieronder krijgt de peildatum aangereikt (AD-14).
 */
export function vandaagInAmsterdam() {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());
}
/** Laatste kalenderdag van de maand waarin `datum` valt, als `YYYY-MM-DD`. */
function eindeVanDeMaand(datum) {
    const parts = datum.split('-').map(Number);
    const year = parts[0] ?? 1970;
    const month = parts[1] ?? 1;
    // Dag 0 van de VOLGENDE maand = de laatste dag van deze maand, met
    // automatische maand- en jaaroverloop.
    return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}
/** `datum` plus zeven kalenderdagen, als `YYYY-MM-DD`. */
function zevenDagenNa(datum) {
    const parts = datum.slice(0, 10).split('-').map(Number);
    const year = parts[0] ?? 1970;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return new Date(Date.UTC(year, month - 1, day + 7)).toISOString().slice(0, 10);
}
/**
 * Einde van de correctietermijn voor verlof met deze laatste verlofdag: het
 * LAATSTE van drie ankers (B14, B22, story 3.2 AC-5).
 *
 * 1. het einde van de kalendermaand van de laatste verlofdag;
 * 2. zeven dagen ná die laatste verlofdag — de ondergrens die voorkomt dat
 *    verlof op de laatste dag van een maand een termijn van (bijna) nul dagen
 *    krijgt;
 * 3. zeven dagen ná `laatsteGoedkeuringOp`, het moment van de laatste
 *    benodigde goedkeuring (FR-7, besluit V7, AD-29/AD-37). De aanroeper vult
 *    dat met `hr_decided_at ?? decided_at`; gebruik daarvoor
 *    {@link laatsteGoedkeuringOp}, en schrijf die uitdrukking niet zelf uit.
 *
 * Het argument heet naar wat het IS en niet naar waar het vandaan komt, omdat
 * het anker over "de laatste goedkeuring" gaat en niet over één kolom.
 *
 * ⚠ **`null` op `laatsteGoedkeuringOp` betekent: dit anker is NIET VAN
 * TOEPASSING** — er is geen goedkeuringsmoment om zeven dagen bij op te
 * tellen, omdat er geen goedkeuring nodig was of omdat er nog geen is. Het
 * derde anker **vervalt** dan en de andere twee bepalen de termijn. `null`
 * betekent uitdrukkelijk NIET dat de termijn nog loopt (besluit 2026-08-12,
 * een omkering van de eerdere lezing): een ontbrekende waarde hoort de regel
 * niet te verzwakken, want dan zet elke aanroeper die het derde argument
 * vergeet stilzwijgend de hele correctietermijn uit. "Er staat nog een
 * goedkeuring open" wordt daarom elders herkend, aan
 * {@link goedkeuringsstand} — zie {@link bevestigingsToestand}.
 *
 * Puur: geen database, geen systeemklok. De `Date.UTC`-aanroepen in de
 * hulpfuncties hierboven zijn kalenderrekenkunde, niet een "huidig moment";
 * ze blijven daarom buiten het verbod op `new Date()` zonder peildatum uit
 * AD-25/AC-6.
 */
export function correctieTermijnEinde(laatsteVerlofdag, laatsteGoedkeuringOp = null) {
    const ankers = [eindeVanDeMaand(laatsteVerlofdag), zevenDagenNa(laatsteVerlofdag)];
    // `null` laat het derde anker vervallen — zie het blok hierboven.
    if (laatsteGoedkeuringOp !== null)
        ankers.push(zevenDagenNa(laatsteGoedkeuringOp));
    // ISO-datums zijn lexicografisch vergelijkbaar, dus dit IS "de laatste".
    return ankers.reduce((laatste, anker) => (anker > laatste ? anker : laatste));
}
/**
 * Is de correctietermijn voor verlof met deze laatste verlofdag, gegeven een
 * peildatum, al verstreken? `peildatum` moet altijd expliciet meegegeven
 * worden (AD-25) — typisch `vandaagInAmsterdam()` bij de aanroeper, nooit
 * hier intern opnieuw berekend, anders is deze functie niet met een vaste
 * datum te testen. Op de einddatum zelf is de termijn nog NIET verstreken
 * (strikt groter-dan).
 *
 * De argumentvolgorde is bewust *(einddatum, peildatum, goedkeuring)*: de
 * eerste twee staan waar story 2.5 ze zette, zodat een bestaande aanroeper er
 * alleen een derde argument bij krijgt. Voor de betekenis van `null` op dat
 * derde argument: zie {@link correctieTermijnEinde}.
 */
export function correctieTermijnVerstreken(laatsteVerlofdag, peildatum, laatsteGoedkeuringOp = null) {
    return peildatum > correctieTermijnEinde(laatsteVerlofdag, laatsteGoedkeuringOp);
}
/**
 * AC-2 — the route that applies to a request, in ONE place.
 *
 * | reden op de rij | route |
 * | --- | --- |
 * | leeg | `leidinggevende` |
 * | de soort waarover HR als enige beslist | `alleen_hr` (besluit V21) |
 * | de soort waar HR altijd naar kijkt | `leidinggevende_dan_hr` |
 * | boven de maandruimte | `leidinggevende_dan_hr` |
 *
 * Empty means: this request has no HR step. A normal state, never an error
 * and never an empty spot on a screen.
 *
 * "HR decides alone" is asked of `beslistAlleenHr` (`types/leave.ts`) and
 * never spelled out here, and never from a comparison against a soort name
 * (KE-9, AD-27) — that vocabulary lives in `types/leave.ts` and nowhere else,
 * this file included. A reason that names a soort carries that soort's own
 * spelling, which is why the lookup there works; the monthly-allowance reason
 * is not a soort, so the lookup misses and the request falls to
 * `leidinggevende_dan_hr` — exactly the table above.
 *
 * Pure: no database, no peildatum, no setting.
 */
export function goedkeuringsroute(hrStapReden) {
    if (hrStapReden == null)
        return 'leidinggevende';
    if (beslistAlleenHr(hrStapReden))
        return 'alleen_hr';
    return 'leidinggevende_dan_hr';
}
/**
 * AC-3 — where a request stands: `{ ligtBij, stand }`, derived, never stored.
 *
 * The tests run in THIS order, first match wins:
 *
 * | toets | `ligtBij` | `stand` |
 * | --- | --- | --- |
 * | `requestStatus = 'afgewezen'` | `niemand` | `afgewezen` |
 * | `requestStatus = 'ingetrokken'` | `niemand` | `ingetrokken` |
 * | route `alleen_hr`, `hrDecidedAt = null` | `hr` | `wacht_op_hr` |
 * | route `leidinggevende_dan_hr`, `decidedAt ≠ null`, `hrDecidedAt = null` | `hr` | `wacht_op_hr` |
 * | `requestStatus = 'goedgekeurd'` | `niemand` | `goedgekeurd` |
 * | anders | `leidinggevende` | `in_behandeling` |
 *
 * WHY THE TWO END STATES COME FIRST, and why that order is not
 * interchangeable: a request that was rejected or withdrawn lies with NOBODY,
 * whatever route it once followed. Move the route tests up and a rejected
 * request with an HR step would report as "waiting on HR" forever.
 *
 * WHY `goedgekeurd` COMES *AFTER* THE ROUTE TESTS, unlike those two. Under
 * AD-30 `goedgekeurd` means *all required approvals in*, so a row that still
 * has an open HR step and yet carries that status is contradicting itself.
 * Reading the status first would let that contradiction pass as "everyone is
 * done", and the correction window in story 3.2 of the verlof-correctie track
 * hangs on this answer: leave that HR has not signed off must never go
 * definitive. So the HR step is checked first, and it is the missing
 * `hrDecidedAt` — not the status — that decides.
 *
 * `wacht_op_hr` is a stand, never a `request_status`: a request waiting on HR
 * keeps `request_status = 'in_afwachting'` in the database. That is why this
 * function can never return `goedgekeurd` for a row that is `in_afwachting`.
 *
 * Every row yields exactly one pair — never `null`, never `undefined`.
 */
export function goedkeuringsstand(rij) {
    if (rij.requestStatus === 'afgewezen')
        return { ligtBij: 'niemand', stand: 'afgewezen' };
    if (rij.requestStatus === 'ingetrokken')
        return { ligtBij: 'niemand', stand: 'ingetrokken' };
    const route = goedkeuringsroute(rij.hrStapReden);
    const hrIsAanZet = route === 'alleen_hr' || (route === 'leidinggevende_dan_hr' && rij.decidedAt != null);
    if (hrIsAanZet && rij.hrDecidedAt == null)
        return { ligtBij: 'hr', stand: 'wacht_op_hr' };
    if (rij.requestStatus === 'goedgekeurd')
        return { ligtBij: 'niemand', stand: 'goedgekeurd' };
    return { ligtBij: 'leidinggevende', stand: 'in_behandeling' };
}
/**
 * AC-8 — the moment of the LAST required approval, as one expression
 * (AD-37): the HR decision when there was an HR step, the manager's decision
 * otherwise.
 *
 * `null` means: there is NO approval moment yet. It emphatically does NOT
 * mean "the correction window has not expired" — story 3.2 of the
 * verlof-correctie track adds seven days to this moment, and reading `null`
 * as "not expired" would keep an unapproved request correctable forever.
 *
 * It lives here and not at the caller on purpose: a second caller writing
 * out the same expression by hand is precisely the second definition KE-11
 * forbids.
 */
export function laatsteGoedkeuringOp(rij) {
    const { decidedAt, hrDecidedAt } = rij;
    const moment = hrDecidedAt ?? decidedAt;
    if (moment == null)
        return null;
    return moment instanceof Date ? moment.toISOString() : moment;
}
/**
 * AC-1/AC-3 — in welke van de vier toestanden staan deze uren?
 *
 * De toetsen lopen in DEZE volgorde, eerste treffer wint:
 *
 * 1. laatste verlofdag vóór de ingangsdatum → `buiten_bereik`. Er is nooit om
 *    een bevestiging gevraagd, dus er valt ook niets niet te bevestigen
 *    (AD-22): dit is uitdrukkelijk iets anders dan *niet bevestigd*.
 * 2. een van de bevestigingsvelden gevuld → `bevestigd`. Ook ná de termijn, en
 *    ook wanneer HR de uren achteraf alsnog corrigeerde (AC-6).
 * 3. de aanvraag is niet (meer) goedgekeurd, of er staat nog een goedkeuring
 *    open → `openstaand`. Er kan nog van alles met deze uren gebeuren, dus ze
 *    staan niet vast (AC-11). "Er staat nog een goedkeuring open" wordt
 *    herkend aan {@link goedkeuringsstand} — de stand *wacht op HR* (AD-29) —
 *    en nooit aan een leeg goedkeuringsmoment.
 * 4. de correctietermijn verstreken → `niet_bevestigd_definitief`, mét de
 *    datum waarop dat gebeurde.
 * 5. anders → `openstaand`: de termijn loopt nog.
 *
 * Er komt geen vijfde waarde bij (AC-11) en er is geen tak voor de verlofsoort
 * (AC-12). De functie kijkt evenmin naar de boekingstoestand: "nog niet
 * geboekt" is iets heel anders dan "wordt nooit geboekt", en dat verschil hier
 * inbouwen is precies de fout die KE-10 verbiedt.
 *
 * Puur en zonder schrijfactie (AC-2/AC-8): geen database, geen systeemklok —
 * `ingangsdatum` en `peildatum` komen als argument binnen (AD-25). Twee
 * aanroepen met dezelfde invoer geven dezelfde uitkomst; de toestand wordt
 * nergens opgeslagen (AD-14).
 */
export function bevestigingsToestand(rij, ingangsdatum, peildatum) {
    if (rij.endDate < ingangsdatum)
        return { toestand: 'buiten_bereik', definitiefGewordenOp: null };
    if (rij.hoursConfirmedAt != null || rij.hoursConfirmedBy != null) {
        return { toestand: 'bevestigd', definitiefGewordenOp: null };
    }
    if (rij.requestStatus !== 'goedgekeurd') {
        return { toestand: 'openstaand', definitiefGewordenOp: null };
    }
    if (goedkeuringsstand(rij).stand === 'wacht_op_hr') {
        return { toestand: 'openstaand', definitiefGewordenOp: null };
    }
    const einde = correctieTermijnEinde(rij.endDate, laatsteGoedkeuringOp(rij));
    if (peildatum > einde) {
        return { toestand: 'niet_bevestigd_definitief', definitiefGewordenOp: einde };
    }
    return { toestand: 'openstaand', definitiefGewordenOp: null };
}
// ── Story 4.1 (verlof-correctie): one classification for "needs attention" (AD-14, AD-37) ──
/**
 * FR-22 / AD-28 — does this row never reach the loonadministratie?
 *
 * The ONE place in the codebase that writes out the fourth, final value of the
 * booking axis. Story 4.3 keeps exactly these rows out of its range by asking
 * this question, instead of writing the same comparison a second time.
 *
 * It reads the ROW STATE, never the leave soort (KE-9). The soort is
 * translated once, at submission time; anything downstream reads the result.
 * Note the name: `soortBoektNooit` is the soort predicate that lives in
 * `types/leave.ts` — this one answers a different question about a different
 * input, and conflating the two is what KE-9 forbids.
 *
 * "No booking yet" and "no booking ever" are two different things, and the
 * difference is recorded positively in the data (KE-10) — which is why this
 * predicate never looks at `nmbrsBookedAt` or `nmbrsLeaveId`.
 */
export function rijBoektNooit(rij) {
    return rij.syncStatus === 'niet_boekend';
}
/**
 * AD-33 — DE reserveringsformule: telt deze rij mee in het verlofsaldo dat een
 * aanvrager en een goedkeurder te zien krijgen?
 *
 * Eén plek, drie lezers. De formule stond tot story 9.3 twee keer woordelijk in
 * `leaveRequestService.ts` (`computeReservedHours` en `checkBalanceOvershoot`);
 * twee kopieën van dezelfde regel lopen uit de pas zodra er één waarde
 * bijkomt, en er kwam er met de vierde waarde van de boek-as precies één bij
 * (KE-9, KE-10).
 *
 * De regel luidt: de aanvraag is `in_afwachting` of `goedgekeurd`, én de
 * boek-as staat op `'pending'` of `'failed'`, én er staat nog niets in de
 * loonadministratie.
 *
 * **De statusvoorwaarde staat POSITIEF, en dat is de kern van deze functie.**
 * `!== 'synced'` betekende tot story 9.1 hetzelfde, maar dekt sindsdien óók de
 * vierde waarde van de boek-as — verlof dat nooit geboekt wordt en dus nooit
 * uit de reservering verdwijnt, waardoor het permanent van het saldo af zou staan
 * (besluit V16, FR-9). Bij de volgende waarde die er ooit bijkomt is
 * stilzwijgend meetellen de gevaarlijke kant en niet-meetellen de veilige.
 * Vereenvoudig deze vorm dus niet naar de negatieve, ook niet "voor de
 * leesbaarheid": dat is precies de fout die deze functie voorkomt.
 *
 * **`nmbrsBookedAt` hoort erbij (AD-19, geërfd uit verlof-correctie).** Uren
 * die al in de loonadministratie staan tellen niet dubbel — óók niet wanneer
 * de boek-as na een goedgekeurde urencorrectie weer op `'pending'` staat
 * (AD-13). Dat is precies het geval waarvoor die voorwaarde ooit is toegevoegd;
 * zonder haar meldt de overschrijdingswaarschuwing daar een valse
 * overschrijding.
 *
 * De functie beantwoordt één vraag over één rij: telt deze rij mee. WELKE
 * urenwaarde er dan geteld wordt — de `override` van de goedkeuringslijst, het
 * `excludeRequestId` van de overschrijdingswaarschuwing — blijft de zaak van
 * de aanroeper.
 *
 * Zuiver: geen database, geen instelling, geen klok. Ze leest `sync_status` en
 * nooit de verlofsoort (AD-28).
 */
export function reserveertVerlofsaldo(rij) {
    const staatOpenInDePortal = rij.requestStatus === 'in_afwachting' || rij.requestStatus === 'goedgekeurd';
    const wachtNogOpDeLoonadministratie = rij.syncStatus === 'pending' || rij.syncStatus === 'failed';
    const nogNietsGeboekt = rij.nmbrsBookedAt == null;
    return staatOpenInDePortal && wachtNogOpDeLoonadministratie && nogNietsGeboekt;
}
/**
 * A koppel reason that can be trusted, from a value that arrived from outside
 * the type system.
 *
 * An absent or unrecognised reason is NOT an error but
 * `geen_overeenkomende_regel`, the safe lower bound: that reason is not worth
 * retrying, and not retrying is never harmful. The opposite default — letting
 * an unknown value look retryable — costs HR time on a hopeless case every
 * time someone presses the button.
 */
function alsKoppelReden(waarde) {
    if (waarde != null && VERLOF_KOPPEL_REDENEN.includes(waarde)) {
        return waarde;
    }
    return 'geen_overeenkomende_regel';
}
/**
 * AC-1 — THE single classification of "needs attention", shared by the HR page
 * (story 4.2) and the team block (story 4.3). Both feed it a different RANGE;
 * neither of them redefines what the groups mean (AD-14). A second place that
 * combines these fields would inevitably pick another order of checks, and
 * then HR counts three where the manager sees two.
 *
 * Only approved leave takes part: anything still pending, rejected or
 * withdrawn has no payroll consequence and lands in no group at all (FR-26).
 *
 * THE ORDER OF THE CHECKS IS BINDING — ⓪ → ① → ③ → ② — and every row lands in
 * at most one group:
 *
 * | # | group | meaning |
 * | --- | --- | --- |
 * | ⓪ | `buitenDeLoonadministratie` | never reaches payroll at all |
 * | ① | `wachtOpLoonadministratie` | the booking is still open or failed |
 * | ③ | `nietGekoppeld` | approved, but no kenmerk to act on |
 * | ② | `urenNietBevestigd` | nobody has confirmed the hours yet |
 *
 * WHY ⓪ COMES FIRST. Such a row is otherwise a candidate for ① *(nothing was
 * ever booked)* AND for ③ *(there is no kenmerk)*, and both readings are
 * wrong: nothing went wrong and nothing was left undone. From the first
 * calamity day after go-live those blocks would fill up with leave that is
 * perfectly fine, which is exactly how an overview becomes useless.
 *
 * WHY ③ COMES BEFORE ②. A row without a kenmerk can by definition never be
 * booked successfully, so it must not also be counted as "hours not yet
 * confirmed" — the double count story 2.8 rules out.
 *
 * TWO STATES NEED NO ACTION AT ALL and are therefore excluded from ① *and* ③
 * (AD-13, first row of the state table): `hours = 0` with nothing ever booked
 * — there is nothing in the loonadministratie and nothing belongs there — and,
 * for ① only, a row that IS booked but carries no kenmerk, which belongs in ③
 * because retrying it can never succeed.
 *
 * THE START DATE ONLY BOUNDS ② (AD-22). Leave whose last day falls before it
 * is out of range for the confirmation loop: no label, no counter. Leave that
 * failed to reach payroll, or that has no kenmerk, still belongs in ①
 * respectively ③ however old it is — that is a real difference with the
 * payslip, and hiding it is what ① exists to prevent. The group boundaries
 * carry no month filter either (B9): the range is the caller's business.
 *
 * PURE, and no clock (AD-25): `peildatum` and `ingangsdatum` come in as
 * arguments, the incoming array and its rows are never written to, and the
 * outcome is a fresh object. Two calls with the same input give the same
 * answer.
 */
export function classificeerVerlofAandacht(rijen, opties) {
    const indeling = {
        wachtOpLoonadministratie: [],
        urenNietBevestigd: [],
        nietGekoppeld: [],
        buitenDeLoonadministratie: [],
    };
    for (const rij of rijen) {
        if (rij.requestStatus !== 'goedgekeurd')
            continue;
        const regel = {
            ...rij,
            redenCode: null,
            redenIsOpnieuwTeProberen: false,
            definitiefOp: null,
            // The calendar month of the last leave day against that of the peildatum
            // — a marker for the screen, never a filter.
            uitEerdereMaand: rij.endDate.slice(0, 7) < opties.peildatum.slice(0, 7),
        };
        // ⓪ hoort hier niet.
        if (rijBoektNooit(rij)) {
            indeling.buitenDeLoonadministratie.push(regel);
            continue;
        }
        const zonderKenmerk = rij.nmbrsLeaveId == null;
        const nooitGeboekt = rij.nmbrsBookedAt == null;
        // AD-13, first state: nothing is in payroll and nothing belongs there.
        const nietsTeDoen = rij.hours === 0 && nooitGeboekt;
        // AD-13, `NMBRS_LEAVE_ID_ONBEKEND`: booked, but there is no handle to act
        // on. Belongs in ③, so ① does not offer a button that can never succeed.
        const geboektZonderKenmerk = !nooitGeboekt && zonderKenmerk;
        // ① wacht op de loonadministratie.
        const doorzettenStaatOpen = rij.syncStatus === 'pending' || rij.syncStatus === 'failed';
        if (doorzettenStaatOpen && !nietsTeDoen && !geboektZonderKenmerk) {
            indeling.wachtOpLoonadministratie.push(regel);
            continue;
        }
        // ③ niet gekoppeld.
        if (zonderKenmerk && !nietsTeDoen) {
            const redenCode = alsKoppelReden(rij.koppelReden);
            indeling.nietGekoppeld.push({
                ...regel,
                redenCode,
                redenIsOpnieuwTeProberen: VERLOF_KOPPEL_REDENEN_OPNIEUW_TE_PROBEREN.includes(redenCode),
            });
            continue;
        }
        // ② uren niet bevestigd.
        const binnenBereik = rij.endDate >= opties.ingangsdatum;
        const verlofIsVoorbij = rij.endDate <= opties.peildatum;
        const termijnLooptNog = !correctieTermijnVerstreken(rij.endDate, opties.peildatum);
        if (rij.hoursConfirmedAt == null &&
            rij.hours > 0 &&
            !rij.heeftCorrectieInBehandeling &&
            binnenBereik &&
            verlofIsVoorbij &&
            termijnLooptNog) {
            indeling.urenNietBevestigd.push({
                ...regel,
                definitiefOp: correctieTermijnEinde(rij.endDate),
            });
        }
    }
    return indeling;
}
/**
 * Story 5.4 (Verlofsoorten, FR-19/KE-9/AD-25): valt deze aanvraag onder de
 * nieuwe verlofsoort-regels?
 *
 * DE ENE afleiding. De serverpoort in `submitLeaveRequest`, het
 * aanvraagformulier en de maandteller uit epic 6 roepen alle drie déze
 * functie aan; niemand schrijft de vergelijking een tweede keer uit.
 *
 * Puur, zoals AD-25 dat eist: geen klok, geen opzoeking in `system_settings`.
 * De ingangsdatum komt als argument binnen — precies zoals AD-15 dat voor het
 * pauzevenster afdwingt.
 *
 * De grens is INCLUSIEF: een startdatum die gelijk is aan de ingangsdatum
 * valt onder de nieuwe regels (FR-19). Zonder ingangsdatum (`null`) is het
 * antwoord altijd onwaar — een ontbrekende instelling betekent UIT, nooit
 * "standaard aan" (KE-8).
 *
 * Beide datums zijn `JJJJ-MM-DD`; die vorm is lexicografisch ordelijk, dus
 * een stringvergelijking volstaat en er hoort geen datumbibliotheek bij.
 *
 * @param startdatum  De STARTdatum van de aanvraag (`JJJJ-MM-DD`) — niet
 *                    "vandaag": een aanvraag die vandaag wordt ingediend voor
 *                    een dag vóór de ingangsdatum valt buiten de nieuwe
 *                    regels, en dat is de bedoeling.
 * @param ingangsdatum De ingestelde ingangsdatum (`JJJJ-MM-DD`), of `null`
 *                    wanneer de instelling ontbreekt of onleesbaar is.
 */
export function valtOnderNieuweVerlofregels(startdatum, ingangsdatum) {
    if (!ingangsdatum)
        return false;
    return startdatum >= ingangsdatum;
}
/**
 * Bestaat deze dag écht op de kalender? (`JJJJ-MM-DD`)
 *
 * Een VORMtoets alleen is niet genoeg, en dat is geen scherpslijperij:
 * `0000-00-00`, `2026-13-05` en `2026-02-30` komen daar allemaal doorheen.
 * Wat er daarna mee gebeurt is per plek anders en overal fout — de
 * verlofinstelling zou de nieuwe regels in één klap voor élke bestaande
 * aanvraag AANzetten (de tekstvergelijking legt `0000-00-00` vóór iedere
 * startdatum), en het maandruimte-eindpunt antwoordt met een maand die niet
 * bestaat: *"Je hebt in 2026-13 nog 3 uur van je 3 uur over."*
 *
 * Hij staat in `packages/shared` omdat beide kanten hem nodig hebben — de
 * beheerdersinstelling in de backend en het queryschema van het
 * maandruimte-eindpunt. Twee formuleringen van dezelfde toets is precies wat
 * KE-9 uitsluit.
 *
 * Zuiver: geen klok, geen tijdzone. De heen-en-terugvergelijking via UTC is
 * de hele truc — een dag die niet bestaat rolt om naar een andere en levert
 * dan een andere tekst op dan hij binnenkwam.
 *
 * Gevonden bij de code reviews van 2026-08-16 en 2026-08-17.
 */
export function isEchteKalenderdatum(raw) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw))
        return false;
    const d = new Date(`${raw}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === raw;
}
/** Maandnamen voluit, voor de uitleg bij de maandteller. Index 0 = januari. */
const MAANDEN_VOLUIT = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
];
/**
 * Een datum in gewone woorden: `'2026-09-01'` wordt `'1 september'`.
 *
 * Het jaartal blijft bewust weg — §10.7 schrijft *"vóór 1 september"*, niet
 * *"vóór 1 september 2026"*, en §10.3 geeft *"Van 29 januari tot en met 2
 * februari"*. Een onherkenbare datum levert de ruwe waarde op in plaats van
 * een halve zin; dat is zichtbaar fout in plaats van stil fout.
 *
 * Story 10.2 gebruikt hem voor de uitkomstregel van bijzonder verlof. Hij staat
 * daarom hier en niet nog een keer in het scherm: `MAANDEN_VOLUIT` bestaat in
 * deze codebase al vier keer, en er komt er geen vijfde bij.
 */
export function datumInWoorden(isoDatum) {
    const delen = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDatum);
    if (!delen)
        return isoDatum;
    const maand = MAANDEN_VOLUIT[Number(delen[2]) - 1];
    if (!maand)
        return isoDatum;
    return `${Number(delen[3])} ${maand}`;
}
/** De ingangsdatum in gewone woorden (§10.7) — dezelfde vorm, eigen naam. */
function ingangsdatumInWoorden(ingangsdatum) {
    return datumInWoorden(ingangsdatum);
}
/**
 * §10.7 (KE-7) — DE uitleg onder de maandteller bij bestaand verlof.
 *
 * De tekst is op 2026-08-10 woordelijk vastgesteld en geldt zoals hij er
 * staat: *"Verlof van vóór 1 september telt niet mee in je 3 uur per maand."*
 * Story 6.1 (de maandteller) neemt hem hiervandaan en verzint hem niet
 * opnieuw — een tweede formulering van dezelfde zin is precies wat KE-7
 * verbiedt, en de zin ís een belofte aan de medewerker over wat zijn ruimte
 * doet.
 *
 * De datum is een VARIABELE, geen letterlijke `1 september`: de ingangsdatum
 * is een instelling (`verlofsoort_ingangsdatum`, story 5.4) en kan verzet
 * worden. Een hardgecodeerde september zou dan een onwaarheid op het scherm
 * zetten.
 *
 * Zuiver, zoals AD-25 eist: geen klok en geen opzoeking. De ingangsdatum komt
 * als argument binnen, in dezelfde `JJJJ-MM-DD`-vorm die
 * {@link valtOnderNieuweVerlofregels} hierboven vergelijkt — vandaar dat deze
 * twee naast elkaar wonen.
 *
 * @param ingangsdatum De ingestelde ingangsdatum (`JJJJ-MM-DD`).
 */
export function tellerUitlegBestaandVerlof(ingangsdatum) {
    return `Verlof van vóór ${ingangsdatumInWoorden(ingangsdatum)} telt niet mee in je 3 uur per maand.`;
}
// ── Story 6.1 (verlofsoorten): DE maandteller voor medisch bezoek (FR-4, AD-34) ──
/**
 * FR-4, besluiten V15 en V34 — de maandruimte, in uren, gelijk voor iedereen.
 *
 * Geen deeltijdfactor, geen dienstduur, geen contract: het getal staat hier
 * één keer, zodat het niet op drie plekken los in de code belandt. Wil de
 * opdrachtgever het ooit zonder uitrol kunnen wijzigen, dan is dat een
 * instelling in `system_settings` en één wijziging hier.
 */
export const MAANDRUIMTE_MEDISCH_BEZOEK_UREN = 3;
/**
 * De statussen waarin een aanvraag nog meetelt in de maandruimte (FR-4).
 *
 * Exact twee, en er komt er geen derde bij: *wacht op HR* is onder AD-29 óók
 * `in_afwachting`, dus de teller heeft geen weet van de HR-stap nodig en wordt
 * daar niet mee belast. Afgewezen en ingetrokken verlof telt niet.
 */
const MAANDTELLER_STATUSSEN = ['in_afwachting', 'goedgekeurd'];
/**
 * De som op het KWARTIER, en met opzet pas NA het optellen (AC-7, B17).
 *
 * De uren op de rijen zijn al veelvouden van een kwartier — `urenUitTijdvak`
 * rondt daar zelf op af — maar een SOM van zulke waarden kan in JavaScript
 * alsnog als `3.0000000000000013` uitkomen, en dat is méér dan drie. Rondt
 * iemand per rij af in plaats van na het optellen, dan verdwijnt elke waarde
 * onder een achtste uur juist volledig. De grens ligt op méér dan 3,00; die
 * vergelijking mag van geen van beide fouten afhangen.
 */
function opHetKwartier(uren) {
    return Math.round(uren * 4) / 4;
}
/**
 * FR-4 — telt deze aanvraag mee in de maandruimte van de gevraagde maand?
 *
 * Vier voorwaarden, en ze moeten alle vier gelden (besluiten V10 en V14):
 *
 * | voorwaarde | waarop |
 * | --- | --- |
 * | de soort telt mee in de maandruimte | `soortTeltMeeInMaandruimte` |
 * | de aanvraag leeft nog | `requestStatus ∈ {in_afwachting, goedgekeurd}` |
 * | hij hoort bij déze maand | de kalendermaand van de STARTdatum |
 * | hij valt onder de nieuwe regels | `valtOnderNieuweVerlofregels` |
 *
 * De soort wordt NOOIT bij naam vergeleken: het antwoord komt uit het gedeelde
 * predicaat op `VERLOFSOORTEN` (AD-27, KE-9). Een aanvraag zonder soort —
 * bestaand verlof, teruggelezen verlof, verlof uit een andere verlofgroep —
 * valt daarmee vanzelf af, zonder een tweede toets (FR-19).
 *
 * De correcties horen bij de handtekening en niet bij het antwoord: ze bepalen
 * WELKE urenwaarde meetelt, nooit óf de rij meetelt. Ze staan hier zodat de
 * twee functies dezelfde vorm dragen en niet uit elkaar kunnen lopen.
 *
 * Zuiver (AD-25): geen database, geen netwerk, geen instelling en geen klok —
 * de maand en de ingangsdatum komen als argument binnen, altijd.
 *
 * @param rij          De verlofrij, in de smalle vorm hierboven.
 * @param correcties   De openstaande urencorrecties; zie {@link maandtellerUren}.
 * @param maand        De REKENmaand, in `JJJJ-MM` — niet de maandnaam.
 * @param ingangsdatum De ingestelde ingangsdatum (`JJJJ-MM-DD`).
 */
export function teltMeeInMaandteller(rij, correcties, maand, ingangsdatum) {
    // De correcties horen bij de HANDTEKENING en niet bij dit antwoord (zie
    // hierboven). Ze staan er zodat de twee functies dezelfde vorm dragen; deze
    // regel is wat `noUnusedParameters` daarvoor vraagt.
    void correcties;
    const { verlofsoort, requestStatus, startDate } = rij;
    if (!soortTeltMeeInMaandruimte(verlofsoort))
        return false;
    if (!MAANDTELLER_STATUSSEN.includes(requestStatus))
        return false;
    if (startDate.slice(0, 7) !== maand)
        return false;
    return valtOnderNieuweVerlofregels(startDate, ingangsdatum);
}
/**
 * FR-4 — hoeveel uur medisch bezoek er in deze maand geteld wordt.
 *
 * DE HOGERE VAN TWEE WAARDEN PER RIJ (AC-6). Hangt er een urencorrectie boven
 * de markt die de uren VERHOOGT, dan telt die voorgestelde waarde mee; anders
 * blijft een aanvraag stilletjes onder de grens zolang de verhoging nog niet
 * beslist is. Een openstaande VERLAGING verlaagt de teller juist niet — het is
 * de hógere van de twee, niet de laatste. Een goedgekeurde correctie zit al in
 * `hours` verwerkt en een afgewezen correctie bestaat niet meer, dus alleen
 * `in_afwachting` doet mee.
 *
 * NIET GESTAPELD OP DE RESERVERINGSFORMULE (AD-33, AD-34). Die telt iets
 * anders, met een andere statusverzameling en met de boek-as erin. Ze blijven
 * twee functies; geboekt medisch bezoek valt uit de reservering maar blijft de
 * maandruimte van die maand gewoon vullen.
 *
 * Zuiver (AD-25), net als {@link teltMeeInMaandteller}.
 *
 * @param rijen        Alle verlofrijen van ÉÉN medewerker die in beeld zijn.
 * @param correcties   Alle correcties op die rijen, elke status; het filteren
 *                     gebeurt hier zodat de aanroeper er geen tweede regel
 *                     voor schrijft.
 * @param maand        De REKENmaand, in `JJJJ-MM` — niet de maandnaam.
 * @param ingangsdatum De ingestelde ingangsdatum (`JJJJ-MM-DD`).
 */
export function maandtellerUren(rijen, correcties, maand, ingangsdatum) {
    let totaal = 0;
    for (const rij of rijen) {
        if (!teltMeeInMaandteller(rij, correcties, maand, ingangsdatum))
            continue;
        let uren = rij.hours;
        for (const correctie of correcties) {
            if (correctie.leaveRequestId !== rij.id)
                continue;
            if (correctie.status !== 'in_afwachting')
                continue;
            if (correctie.newHours > uren)
                uren = correctie.newHours;
        }
        totaal += uren;
    }
    return opHetKwartier(totaal);
}
//# sourceMappingURL=workabilityResolver.js.map